import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Flag, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EarningsNotice } from "@/components/EarningsNotice";

export default function License() {
  const { user, refreshUser } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const nextUrl = sp.get("next") || "";
  const [license, setLicense] = useState(null);
  const [opts, setOpts] = useState({ carriers: [], manufacturers: [], processors: [] });
  const [form, setForm] = useState({ email: "", age: "", phone: "", phone_carrier: "", phone_manufacturer: "", payment_processor: "" });
  const [applied, setApplied] = useState(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpDone, setOtpDone] = useState(false);
  const [noticeAcked, setNoticeAcked] = useState(false);

  useEffect(() => {
    api.get("/license/me").then((r) => setLicense(r.data)).catch(() => {});
    api.get("/license/options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.email) setForm((f) => ({ ...f, email: f.email || user.email })); }, [user]);

  useEffect(() => {
    if (!applied) return;
    const t = setInterval(() => {
      const s = Math.max(0, Math.floor((new Date(applied.expires_at) - new Date()) / 1000));
      setLeft(s);
      if (s <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [applied]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.email || !form.age || !form.phone || !form.phone_carrier || !form.phone_manufacturer || !form.payment_processor)
      return toast.error("Please complete every field.");
    setBusy(true);
    try {
      const { data } = await api.post("/license/apply", { ...form, age: Number(form.age) });
      setApplied(data);
      setOtpDone(false);
      setOtpCode("");
      toast.success(
        data.otp_required ? "We texted a code to your phone."
          : data.email_sent ? "Verification sent to your email."
            : "Scan or tap the QR on your phone to verify."
      );
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmOtp = async () => {
    if (otpCode.trim().length < 4) return toast.error("Enter the code we texted you.");
    setBusy(true);
    try {
      await api.post("/license/confirm-otp", { token: applied.token, code: otpCode.trim() });
      setOtpDone(true);
      toast.success("Phone verified - now activate on your racing phone.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const restart = () => { setApplied(null); setOtpDone(false); setOtpCode(""); setNoticeAcked(false); };

  if (license) {
    return (
      <div className="max-w-md mx-auto animate-slide-up">
        <h1 className="font-heading text-5xl tracking-tight mb-6 flex items-center gap-2"><ShieldCheck className="text-race-volt" /> BR LICENSE</h1>
        <div className="relative bg-race-elevated border border-race-line p-6 overflow-hidden" data-testid="br-license-card">
          <div className="absolute top-0 right-0 w-1 h-full bg-race-red" />
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="overline text-race-red">IBRA · Text Racing</div>
              <div className="font-heading text-4xl tracking-tight">{license.holder_name}</div>
            </div>
            <Flag className="text-race-red" size={28} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Info label="License No" value={license.license_no} />
            <Info label="CPS Rating" value={user?.avg_cps ?? 0} accent />
            <Info label="Standing" value={(license.status || "").replace("_", " ")} />
            <Info label="Carrier" value={license.phone_carrier || "-"} />
            <Info label="Device" value={license.phone_manufacturer || "-"} />
            <Info label="Payout" value={license.payment_processor || "-"} />
          </div>
        </div>
        <p className="text-xs text-race-muted mt-4 flex items-center gap-1"><Smartphone size={12} /> Verified & bound to your phone.</p>
        {nextUrl && (
          <button onClick={() => {
              let done = null;
              try { done = localStorage.getItem("tr_interests_onboarded"); } catch (e) { /* ignore */ }
              navigate(done ? nextUrl : `/onboarding/interests?next=${encodeURIComponent(nextUrl)}`);
            }} data-testid="license-continue"
            className="w-full mt-5 bg-race-volt text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm hover:opacity-90 transition-opacity">
            CONTINUE TO YOUR RACE →
          </button>
        )}
      </div>
    );
  }

  if (applied && applied.otp_required && !otpDone) {
    return (
      <div className="max-w-md mx-auto animate-slide-up text-center" data-testid="license-otp-panel">
        <h1 className="font-heading text-4xl tracking-tight mb-2">VERIFY YOUR PHONE</h1>
        <p className="text-race-sub text-sm mb-6">We texted a code to <span className="text-race-txt font-mono">{form.phone}</span>. Enter it below to verify your number.</p>
        <input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
          inputMode="numeric" maxLength={10} placeholder="Enter code" data-testid="license-otp-input"
          className="w-full text-center tracking-[0.4em] font-mono text-2xl bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-3 rounded-sm text-race-txt" />
        <button onClick={confirmOtp} disabled={busy} data-testid="license-otp-confirm"
          className="w-full mt-4 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          {busy ? "…" : "CONFIRM CODE"}
        </button>
        <button onClick={restart} data-testid="license-otp-restart"
          className="mt-4 text-xs text-race-sub underline">Start over</button>
      </div>
    );
  }

  if (applied && !noticeAcked) {
    return (
      <div className="max-w-lg mx-auto" data-testid="license-notice-step">
        <EarningsNotice onAck={() => setNoticeAcked(true)} />
      </div>
    );
  }

  if (applied) {
    const expired = left <= 0;
    return (
      <div className="max-w-md mx-auto animate-slide-up text-center" data-testid="license-verify-panel">
        <h1 className="font-heading text-4xl tracking-tight mb-2">VERIFY ON YOUR PHONE</h1>
        <p className="text-race-sub text-sm mb-6">Scan or tap the code with the phone you'll race on. This binds your BR License to that device.</p>
        <div className="inline-block bg-white p-4 rounded-md">
          <QRCodeSVG value={applied.verify_url} size={200} fgColor="#0B0B0C" bgColor="#FFFFFF" data-testid="license-qr" />
        </div>
        <div className={`mt-4 font-mono text-sm ${expired ? "text-race-red" : "text-race-sub"}`} data-testid="license-countdown">
          {expired ? "Link expired - request a new one." : `Expires in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`}
        </div>
        <a href={applied.verify_url} className="mt-4 inline-block text-xs text-race-sub underline break-all" data-testid="license-verify-link">{applied.verify_url}</a>
        <div className="mt-6">
          <button onClick={restart} data-testid="license-restart"
            className="border border-race-line hover:border-race-txt text-race-txt px-6 py-2 rounded-sm transition-colors">
            {expired ? "Start over" : "Edit details"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto animate-slide-up" data-testid="license-apply-form">
      <h1 className="font-heading text-5xl tracking-tight mb-2 flex items-center gap-2"><Flag className="text-race-red" /> BR LICENSE</h1>
      <p className="text-race-sub text-sm mb-6">Required to save stats, appear on leaderboards, and enter IBRA contests. Must be 18+.</p>
      <div className="bg-race-surface border border-race-line p-6 flex flex-col gap-4">
        <Field label="Email"><input type="email" value={form.email} onChange={set("email")} data-testid="license-email" className={inputCls} /></Field>
        <Field label="Age"><input type="number" min="18" value={form.age} onChange={set("age")} data-testid="license-age" className={inputCls} /></Field>
        <Field label="Mobile Number"><input value={form.phone} onChange={set("phone")} placeholder="+1 555 123 4567" data-testid="license-phone" className={inputCls} /></Field>
        <Field label="Phone Carrier"><Select value={form.phone_carrier} onChange={set("phone_carrier")} options={opts.carriers} testid="license-carrier" /></Field>
        <Field label="Phone Manufacturer"><Select value={form.phone_manufacturer} onChange={set("phone_manufacturer")} options={opts.manufacturers} testid="license-manufacturer" /></Field>
        <Field label="Preferred Payment Processor"><Select value={form.payment_processor} onChange={set("payment_processor")} options={opts.processors} testid="license-processor" /></Field>
        <button onClick={submit} disabled={busy} data-testid="license-apply-btn"
          className="bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          {busy ? "…" : "SEND VERIFICATION"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-race-txt";
const Field = ({ label, children }) => (<div><label className="overline block mb-1">{label}</label>{children}</div>);
const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={onChange} data-testid={testid} className={inputCls}>
    <option value="">Select…</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Info = ({ label, value, accent }) => (
  <div><div className="overline">{label}</div>
    <div className={`font-mono text-sm capitalize ${accent ? "text-race-volt" : "text-race-txt"}`}>{value}</div></div>
);
